import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { FaSearch, FaFilter, FaHeart, FaEye, FaCar, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';
import CarDetailPage from './CarDetailPage';
import { getCars, CarListing, subscribeToCars } from '../../firebase/firestore';

const Container = styled.div`
  padding: ${theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.md};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
  }
`;

const SearchButton = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const FilterButton = styled.button`
  background: ${theme.colors.white};
  color: ${theme.colors.secondary};
  border: 2px solid ${theme.colors.lightGray};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const CarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const CarCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const CarImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, ${theme.colors.lightGray} 0%, ${theme.colors.mediumGray} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.mediumGray};
  font-size: 3rem;
  position: relative;
`;

const FavoriteButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: rgba(255, 255, 255, 0.9);
  border: none;
  padding: ${theme.spacing.sm};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.mediumGray};
  transition: all 0.3s ease;
  
  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.white};
  }
`;

const CarContent = styled.div`
  padding: ${theme.spacing.lg};
`;

const CarTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.sm};
`;

const CarDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.md};
`;

const CarDetail = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.mediumGray};
  font-size: 0.9rem;
`;

const CarPrice = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const CarActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  
  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
    border: none;
    
    &:hover {
      background: ${theme.colors.secondary};
      transform: translateY(-2px);
    }
  ` : `
    background: ${theme.colors.white};
    color: ${theme.colors.secondary};
    border: 2px solid ${theme.colors.lightGray};
    
    &:hover {
      border-color: ${theme.colors.primary};
      color: ${theme.colors.primary};
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.mediumGray};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.lightGray};
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.secondary};
`;

const EmptyDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const CarsListingsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [cars, setCars] = useState<CarListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    
    // Set up real-time listener for cars
    const unsubscribe = subscribeToCars((carsData) => {
      // Filter out cars with missing required fields
      const validCars = carsData.filter(car => 
        car.title && 
        car.make && 
        car.model && 
        car.year && 
        car.price !== undefined && 
        car.currency && 
        car.location && 
        car.mileage !== undefined
      );
      setCars(validCars);
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
  }, []);

  const filteredCars = cars.filter(car =>
    car.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    car.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
    car.model.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCarClick = (carId: string) => {
    setSelectedCarId(carId);
  };

  const handleFavoriteToggle = (carId: string) => {
    // Toggle favorite status
    console.log('Toggle favorite:', carId);
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading cars...</div>
        </div>
      </Container>
    );
  }

  if (selectedCarId) {
    const car = cars.find(c => c.id === selectedCarId);
    if (!car) return null;
    
    return (
      <CarDetailPage
        car={car}
        onBack={() => setSelectedCarId(null)}
      />
    );
  }

  return (
    <Container>
      <Header>
        <Title>Cars Marketplace</Title>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search for cars..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchButton>
            <FaSearch />
            Search
          </SearchButton>
          <FilterButton>
            <FaFilter />
            Filter
          </FilterButton>
        </SearchContainer>
      </Header>

      {filteredCars.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FaCar />
          </EmptyIcon>
          <EmptyTitle>No cars found</EmptyTitle>
          <EmptyDescription>
            Try adjusting your search terms or filters to find the perfect car.
          </EmptyDescription>
        </EmptyState>
      ) : (
        <CarsGrid>
          {filteredCars.map((car) => (
            <CarCard key={car.id} onClick={() => handleCarClick(car.id)}>
              <CarImage>
                <FaCar />
                <FavoriteButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFavoriteToggle(car.id);
                  }}
                >
                  <FaHeart style={{ color: 'inherit' }} />
                </FavoriteButton>
              </CarImage>
              <CarContent>
                <CarTitle>{car.title}</CarTitle>
                <CarDetails>
                  <CarDetail>
                    <FaMapMarkerAlt />
                    {car.location}
                  </CarDetail>
                  <CarDetail>
                    <FaCalendarAlt />
                    {car.year} • {car.mileage.toLocaleString()} km
                  </CarDetail>
                  <CarDetail>
                    {car.fuelType} • {car.transmission}
                  </CarDetail>
                </CarDetails>
                <CarPrice>{car.currency} {car.price.toLocaleString()}</CarPrice>
                <CarActions>
                  <ActionButton variant="secondary">
                    <FaHeart />
                    Save
                  </ActionButton>
                  <ActionButton variant="primary" onClick={() => handleCarClick(car.id)}>
                    <FaEye />
                    View Details
                  </ActionButton>
                </CarActions>
              </CarContent>
            </CarCard>
          ))}
        </CarsGrid>
      )}
    </Container>
  );
};

export default CarsListingsPage;

